import { FlaskConical, Clock, Scissors, CheckCircle2, FileText, Music, Download, ExternalLink, StickyNote, Banknote } from "lucide-react";
import { fileUrl } from "@/constants/roles";

// Renders a Test Pack exactly as the applicant sees it. Used by the applicant dashboard
// (with the assignment's frozen snapshot) and by the owner's "preview applicant view".
export const TestPackView = ({ pack, version }) => {
  if (!pack) return null;
  const Section = ({ icon: Icon, title, children, testid }) => (
    <div data-testid={testid} className="mt-4 rounded-2xl bg-[var(--pv-subtle)] p-5">
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]"><Icon className="h-3.5 w-3.5 text-violet-400" /> {title}</p>
      <div className="mt-2 text-sm leading-relaxed text-[var(--pv-text-2)]">{children}</div>
    </div>
  );
  return (
    <div data-testid="test-pack-view">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-display text-lg font-bold text-[var(--pv-text)]">{pack.title}</p>
        {version != null && <span className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-violet-300">Test pack v{version}</span>}
      </div>

      <div data-testid="test-pack-pay-rule" className="mt-4 flex items-start gap-2.5 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
        <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
        <p className="text-xs leading-relaxed text-amber-200/90">
          <b>Paid test — read first:</b> this animation test pays the agreed <b>${(pack.payment_amount ?? 20)} USD</b> once your
          submitted test is reviewed and accepted as a <b>legitimate completed attempt</b> of this brief. Payment does not depend
          on passing the quality bar — but incomplete, off-brief or non-genuine submissions are not payable. This is separate
          from normal production pricing ($55/$110 minimums do not apply to hiring tests).
        </p>
      </div>

      {(pack.required_length || pack.time_allowed) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {pack.required_length && (
            <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]"><Scissors className="h-3 w-3" /> Required length</p>
              <p data-testid="pack-required-length" className="mt-1 text-sm font-bold text-[var(--pv-text)]">{pack.required_length}</p>
            </div>
          )}
          {pack.time_allowed && (
            <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]"><Clock className="h-3 w-3" /> Time allowed</p>
              <p data-testid="pack-time-allowed" className="mt-1 text-sm font-bold text-[var(--pv-text)]">{pack.time_allowed}</p>
            </div>
          )}
        </div>
      )}

      {pack.instructions && <Section icon={FlaskConical} title="Instructions" testid="pack-instructions"><span className="whitespace-pre-wrap">{pack.instructions}</span></Section>}
      {pack.script && <Section icon={FileText} title="Script" testid="pack-script"><span className="whitespace-pre-wrap">{pack.script}</span></Section>}
      {pack.audio_path && (
        <Section icon={Music} title="Finished voice / audio" testid="pack-audio">
          <audio data-testid="pack-audio-player" controls src={fileUrl(pack.audio_path)} className="w-full" />
        </Section>
      )}
      {(pack.asset_paths?.length > 0 || pack.reference_files?.length > 0) && (
        <Section icon={Download} title="Avatars / assets / files" testid="pack-assets">
          <div className="flex flex-wrap gap-2">
            {[...(pack.asset_paths || []), ...(pack.reference_files || [])].map((p) => (
              <a key={p} data-testid={`pack-asset-${p.split("/").pop()}`} href={fileUrl(p)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3.5 py-1.5 text-xs font-bold text-violet-300 hover:bg-violet-500/20">
                <Download className="h-3 w-3" /> {p.split("/").pop()}
              </a>
            ))}
          </div>
        </Section>
      )}
      {pack.reference_urls?.length > 0 && (
        <Section icon={ExternalLink} title="Reference videos" testid="pack-references">
          <div className="space-y-1.5">
            {pack.reference_urls.map((r, i) => (
              <a key={i} data-testid={`pack-ref-${i}`} href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300">
                <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {r.title || r.url}
              </a>
            ))}
          </div>
        </Section>
      )}
      {pack.export_requirements && <Section icon={CheckCircle2} title="Export requirements" testid="pack-export"><span className="whitespace-pre-wrap">{pack.export_requirements}</span></Section>}
      {pack.quality_requirements && <Section icon={CheckCircle2} title="Quality standard (what passing requires)" testid="pack-quality"><span className="whitespace-pre-wrap">{pack.quality_requirements}</span></Section>}
      {pack.additional_notes && <Section icon={StickyNote} title="Additional notes" testid="pack-notes"><span className="whitespace-pre-wrap">{pack.additional_notes}</span></Section>}
    </div>
  );
};
